import { useMemo, useState } from "react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Layout";
import { computeIdrResults, idrClinicalReading } from "@/lib/idrScoring";
import { CLES_DRIVERS, DIFFICULTE } from "@/data/idrCles";
import { DRIVER_BY_KEY } from "@/components/exo/driversExoShared";
import { downloadElementAsPdf } from "@/lib/pdf";

const ID = "idr-patient-resultats";

// Écran de résultats adapté au patient, débloqué par le praticien après la séance (jamais la note du praticien).
export const IdrPatientResultats = ({ idr }) => {
  const results = useMemo(() => computeIdrResults(idr.answers, "fr"), [idr]);
  const reading = useMemo(() => idrClinicalReading(results, "fr"), [results]);
  const r = idr.rankings || {};
  const final = r.antidotes || r.dimensions || r.definitions || [];
  const diff = idr.difficulties || {};
  const [busy, setBusy] = useState(false);
  const pdf = async () => {
    setBusy(true);
    try { await downloadElementAsPdf(document.getElementById(ID), "mes-drivers-relationnels.pdf", { footer: "Inventaire des drivers relationnels — résultats partagés par le praticien · M2HAT Psychologie" }); toast.success("PDF téléchargé"); }
    catch { toast.error("Génération du PDF impossible"); }
    setBusy(false);
  };
  return (
    <div className="text-left" data-testid="idr-patient-resultats">
      <div id={ID} className="space-y-6 bg-[var(--cream)] p-1">
        <div className="hidden print:block"><Brand compact /></div>
        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-7">
          <h3 className="font-serif-title text-3xl text-[var(--gold)] text-center mb-1">Vos résultats</h3>
          <p className="text-sm text-[var(--ink-soft)] text-center mb-4">Partagés par votre praticien après votre séance. Il n'y a pas de bon ou de mauvais driver : ce profil décrit ce qui vous anime.</p>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={results.radarData} outerRadius="60%" margin={{ top: 8, right: 28, bottom: 8, left: 28 }}>
              <PolarGrid stroke="#d8ccbe" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: "#6E5C53", fontSize: 11 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#a1907f", fontSize: 9 }} angle={90} />
              <Radar dataKey="value" stroke="#B58C67" fill="#B58C67" fillOpacity={0.45} isAnimationActive={false} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="idr-patient-scores">
          {results.sorted.map((row) => (
            <div key={row.key} className="rounded-2xl bg-white/70 border border-[var(--border)] p-4" data-testid={`idr-patient-score-${row.key}`}>
              <div className="flex items-baseline justify-between mb-1"><h4 className="font-serif-title text-xl" style={{ color: row.color }}>{row.name}</h4><span className="font-semibold text-[var(--ink)]">{row.score} %</span></div>
              <div className="h-2 w-full rounded-full bg-[var(--beige)] overflow-hidden mb-2"><div className="h-full rounded-full" style={{ width: `${row.score}%`, backgroundColor: row.color }} /></div>
              <span className="inline-block rounded-full px-2.5 py-0.5 text-xs text-white" style={{ backgroundColor: row.interp.color }}>{row.interp.label}</span>
              <p className="text-xs text-[var(--ink-soft)] mt-2">{row.description}</p>
            </div>
          ))}
        </div>
        <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-5 sm:p-7" data-testid="idr-patient-reading">
          <h4 className="font-serif-title text-2xl text-[var(--gold)] mb-3">Lecture descriptive</h4>
          <p className="text-[var(--ink)] leading-relaxed">{reading}</p>
        </div>
        {final.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-4" data-testid="idr-patient-ranking">
              <p className="text-sm font-semibold text-[var(--gold)] mb-2">Votre classement final</p>
              <ol className="space-y-1.5">{final.map((k, i) => <li key={k} className="flex items-center gap-2 text-sm text-[var(--ink)]"><span className="h-6 w-6 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ background: DRIVER_BY_KEY[k]?.color }}>{i + 1}</span>{CLES_DRIVERS[k]?.name}</li>)}</ol>
            </div>
            <div className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-4" data-testid="idr-patient-antidotes">
              <p className="text-sm font-semibold text-[var(--gold)] mb-2">Vos antidotes et leur difficulté aujourd'hui</p>
              <ul className="space-y-1.5">{final.map((k) => { const d = CLES_DRIVERS[k]; const lv = DIFFICULTE.find((x) => x.value === diff[k]); return d ? <li key={k} className="flex items-start justify-between gap-2 text-sm"><span><span className="font-semibold" style={{ color: DRIVER_BY_KEY[k]?.color }}>{d.name}</span> <span className="text-[var(--ink-soft)]">— {d.antidoteShort}</span></span>{lv && <span className="text-xs font-semibold text-[var(--ink)] whitespace-nowrap">{lv.label}</span>}</li> : null; })}</ul>
            </div>
          </div>
        )}
      </div>
      <div className="text-center mt-6">
        <button onClick={pdf} disabled={busy} data-testid="idr-patient-pdf" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} Télécharger mes résultats (PDF)</button>
      </div>
    </div>
  );
};
